import { cn } from '@/lib/utils';
import { PANEL } from '@/lib/surface';
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Package,
  Truck,
  Plus,
  X,
  Search,
  Sparkles,
  Save,
  ArrowRight,
  TrendingUp,
  GripVertical,
  MapPin,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { PageHeader } from '@/components/common';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { loadBuilderApi } from '@/api/services/tms-extended.api';
import { stagger, fadeUp } from '@/lib/animations';

interface Load {
  id: string;
  loadNumber: string;
  equipmentType: string;
  maxWeight: number;
  maxVolume: number;
  maxPallets?: number;
  currentWeight: number;
  currentVolume: number;
  currentPallets: number;
  utilizationPct: number;
  status: string;
  shipmentLoads?: ShipmentLoad[];
}

interface ShipmentLoad {
  id: string;
  shipmentId: string;
  sequence: number;
  weight: number;
  volume: number;
  pallets?: number;
  shipment?: {
    trackingNumber: string;
    origin: { city: string; state: string };
    destination: { city: string; state: string };
  };
}

interface AvailableShipment {
  id: string;
  trackingNumber: string;
  origin: { city: string; state: string };
  destination: { city: string; state: string };
  totalWeight: number;
  totalVolume: number;
  status: string;
}

// Sortable shipment card for drag-and-drop
function SortableShipmentCard({
  shipmentLoad,
  onRemove,
}: {
  shipmentLoad: ShipmentLoad;
  onRemove: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: shipmentLoad.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(PANEL, 'p-3 hover:bg-muted/50 transition-colors')}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3 flex-1">
          <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing mt-1">
            <GripVertical className="w-4 h-4 text-muted-foreground" />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-xs">
                #{shipmentLoad.sequence}
              </Badge>
              <span className="font-medium text-sm truncate">
                {shipmentLoad.shipment?.trackingNumber || shipmentLoad.shipmentId}
              </span>
            </div>

            <div className="mt-1 text-xs text-muted-foreground flex items-center gap-4">
              <div className="flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                <span>{shipmentLoad.shipment?.origin.city || 'N/A'}</span>
                <ArrowRight className="w-3 h-3" />
                <span>{shipmentLoad.shipment?.destination.city || 'N/A'}</span>
              </div>
            </div>

            <div className="mt-2 flex gap-3 text-xs">
              <span>{shipmentLoad.weight.toFixed(0)} lbs</span>
              <span>{shipmentLoad.volume.toFixed(1)} cu ft</span>
              {shipmentLoad.pallets && <span>{shipmentLoad.pallets} pallets</span>}
            </div>
          </div>
        </div>

        <Button
          variant="ghost"
          size="icon"
          aria-label="Dismiss"
          className="h-6 w-6 text-destructive"
          onClick={onRemove}
        >
          <X className="w-3 h-3" />
        </Button>
      </div>
    </div>
  );
}

export default function LoadBuilder() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [load, setLoad] = useState<Load | null>(null);
  const [availableShipments, setAvailableShipments] = useState<AvailableShipment[]>([]);
  const [selectedShipmentId, setSelectedShipmentId] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [isOptimizing, setIsOptimizing] = useState(false);

  // Equipment configuration
  const [equipmentType, setEquipmentType] = useState('DRY_VAN');
  const [maxWeight, setMaxWeight] = useState(45000);
  const [maxVolume, setMaxVolume] = useState(3000);
  const [maxPallets, setMaxPallets] = useState(26);

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Load available shipments
  const loadAvailableShipments = useCallback(async () => {
    if (!user?.companyId) return;

    try {
      const shipments = await loadBuilderApi.getAvailableShipments(user.companyId);
      // Transform API response to match component's AvailableShipment interface
      const transformedShipments: AvailableShipment[] = shipments.map((s) => ({
        id: s.id,
        trackingNumber: s.shipmentNumber,
        origin: { city: s.originCity, state: '' },
        destination: { city: s.destinationCity, state: '' },
        totalWeight: s.weight,
        totalVolume: s.volume,
        status: s.status,
      }));
      setAvailableShipments(transformedShipments);
    } catch (error) {
      console.error('Error loading available shipments:', error);
      toast.error('Failed to load available shipments');
    }
  }, [user?.companyId]);

  useEffect(() => {
    if (user?.companyId) {
      loadAvailableShipments();
    }
  }, [user?.companyId, loadAvailableShipments]);

  const handleCreateLoad = async () => {
    if (!user?.companyId) {
      toast.error('Company information not found');
      return;
    }

    setIsLoading(true);
    try {
      const newLoad = await loadBuilderApi.create({
        equipmentType,
        maxWeight,
        maxVolume,
        maxPallets,
      });
      setLoad(newLoad);
      toast.success('Load created successfully');
    } catch (error: any) {
      console.error('Error creating load:', error);
      toast.error(error?.response?.data?.message || 'Failed to create load');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddShipment = async () => {
    if (!load || !selectedShipmentId) return;

    const shipment = availableShipments.find((s) => s.id === selectedShipmentId);
    if (!shipment) return;

    setIsLoading(true);
    try {
      const updatedLoad = await loadBuilderApi.addShipment(
        load.id,
        shipment.id,
        shipment.totalWeight,
        shipment.totalVolume,
        0 // pallets - could be enhanced to get from shipment data
      );
      setLoad(updatedLoad);
      setSelectedShipmentId('');
      toast.success('Shipment added to load');
    } catch (error: any) {
      console.error('Error adding shipment:', error);
      toast.error(error?.response?.data?.message || 'Failed to add shipment');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveShipment = async (shipmentId: string) => {
    if (!load) return;

    setIsLoading(true);
    try {
      const updatedLoad = await loadBuilderApi.removeShipment(load.id, shipmentId);
      setLoad(updatedLoad);
      toast.success('Shipment removed from load');
    } catch (error: any) {
      console.error('Error removing shipment:', error);
      toast.error(error?.response?.data?.message || 'Failed to remove shipment');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOptimize = async () => {
    if (!load) return;

    setIsOptimizing(true);
    try {
      const optimizedLoad = await loadBuilderApi.optimize(load.id);
      setLoad(optimizedLoad);
      toast.success('Load optimized successfully');
    } catch (error: any) {
      console.error('Error optimizing load:', error);
      toast.error(error?.response?.data?.message || 'Failed to optimize load');
    } finally {
      setIsOptimizing(false);
    }
  };

  const handleSaveLoad = async () => {
    if (!load) return;

    try {
      await loadBuilderApi.update(load.id, { status: 'ASSIGNED' });
      toast.success('Load saved successfully');
      navigate('/tms');
    } catch (error: any) {
      console.error('Error saving load:', error);
      toast.error(error?.response?.data?.message || 'Failed to save load');
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id && load?.shipmentLoads) {
      const oldIndex = load.shipmentLoads.findIndex((sl) => sl.id === active.id);
      const newIndex = load.shipmentLoads.findIndex((sl) => sl.id === over.id);

      const reorderedShipments = arrayMove(load.shipmentLoads, oldIndex, newIndex);

      // Update sequences
      const updatedShipments = reorderedShipments.map((sl, idx) => ({
        ...sl,
        sequence: idx + 1,
      }));

      setLoad({
        ...load,
        shipmentLoads: updatedShipments,
      });
    }
  };

  const weightUtilization = load ? (load.currentWeight / load.maxWeight) * 100 : 0;
  const volumeUtilization = load ? (load.currentVolume / load.maxVolume) * 100 : 0;
  const palletUtilization =
    load && load.maxPallets ? (load.currentPallets / load.maxPallets) * 100 : 0;

  return (
    <motion.main className="space-y-5" variants={stagger} initial="hidden" animate="show">
      <motion.div variants={fadeUp}>
        <PageHeader
          title="Load Builder"
          description="Consolidation of orders into loads, with weight and volume measured against vehicle capacity."
          actions={
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => navigate('/tms')}>
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
              {load && (
                <>
                  <Button
                    variant="outline"
                    onClick={handleOptimize}
                    disabled={
                      !load.shipmentLoads || load.shipmentLoads.length === 0 || isOptimizing
                    }
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    {isOptimizing ? 'Optimizing...' : 'Auto-Optimize'}
                  </Button>
                  <Button onClick={handleSaveLoad}>
                    <Save className="w-4 h-4 mr-2" />
                    Save Load
                  </Button>
                </>
              )}
            </div>
          }
        />
      </motion.div>

      <motion.div variants={fadeUp}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Panel: Load Configuration or Available Shipments */}
          <div className="lg:col-span-1 space-y-6">
            {!load ? (
              // Load Configuration
              <Card>
                <CardHeader>
                  <h3 className="text-base font-semibold text-foreground">
                    <Truck className="w-5 h-5" />
                    New Load
                  </h3>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Equipment Type</Label>
                    <Select value={equipmentType} onValueChange={setEquipmentType}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="DRY_VAN">Dry Van</SelectItem>
                        <SelectItem value="REEFER">Reefer</SelectItem>
                        <SelectItem value="FLATBED">Flatbed</SelectItem>
                        <SelectItem value="STEP_DECK">Step Deck</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Max Weight (lbs)</Label>
                    <Input
                      type="number"
                      value={maxWeight}
                      onChange={(e) => setMaxWeight(Number(e.target.value))}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Max Volume (cu ft)</Label>
                    <Input
                      type="number"
                      value={maxVolume}
                      onChange={(e) => setMaxVolume(Number(e.target.value))}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Max Pallets</Label>
                    <Input
                      type="number"
                      value={maxPallets}
                      onChange={(e) => setMaxPallets(Number(e.target.value))}
                    />
                  </div>

                  <Button className="w-full" onClick={handleCreateLoad} disabled={isLoading}>
                    <Plus className="w-4 h-4 mr-2" />
                    Create Load
                  </Button>
                </CardContent>
              </Card>
            ) : (
              // Available Shipments
              <Card>
                <CardHeader>
                  <h3 className="text-base font-semibold text-foreground">
                    <Package className="w-5 h-5" />
                    Available Shipments
                  </h3>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input placeholder="Search shipments..." className="pl-9" />
                  </div>

                  <div className="space-y-2">
                    <Label>Select Shipment</Label>
                    <Select value={selectedShipmentId} onValueChange={setSelectedShipmentId}>
                      <SelectTrigger>
                        <SelectValue placeholder="Choose a shipment" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableShipments.map((shipment) => (
                          <SelectItem key={shipment.id} value={shipment.id}>
                            {shipment.trackingNumber} - {shipment.totalWeight.toFixed(0)} lbs
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <Button
                    className="w-full"
                    onClick={handleAddShipment}
                    disabled={!selectedShipmentId || isLoading}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add to Load
                  </Button>

                  <div className="text-xs text-muted-foreground text-center">
                    {availableShipments.length} shipments available
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right Panel: Load Builder */}
          <div className="lg:col-span-2 space-y-6">
            {load ? (
              <>
                {/* Capacity Indicators */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <h3 className="text-base font-semibold text-foreground">
                        <TrendingUp className="w-5 h-5" />
                        Load #{load.loadNumber}
                      </h3>
                      <Badge>{load.status}</Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-sm">
                          <span>Weight</span>
                          <span className="font-medium">
                            {load.currentWeight.toFixed(0)} / {load.maxWeight} lbs
                          </span>
                        </div>
                        <div className="h-2 bg-muted rounded-full overflow-hidden">
                          <div
                            className={`h-full transition-all ${
                              weightUtilization > 90
                                ? 'bg-destructive'
                                : weightUtilization > 75
                                  ? 'bg-warning-500'
                                  : 'bg-success-500'
                            }`}
                            style={{ width: `${Math.min(weightUtilization, 100)}%` }}
                          />
                        </div>
                        <div className="text-xs text-muted-foreground text-center">
                          {weightUtilization.toFixed(1)}%
                        </div>
                      </div>

                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-sm">
                          <span>Volume</span>
                          <span className="font-medium">
                            {load.currentVolume.toFixed(0)} / {load.maxVolume} cu ft
                          </span>
                        </div>
                        <div className="h-2 bg-muted rounded-full overflow-hidden">
                          <div
                            className={`h-full transition-all ${
                              volumeUtilization > 90
                                ? 'bg-destructive'
                                : volumeUtilization > 75
                                  ? 'bg-warning-500'
                                  : 'bg-success-500'
                            }`}
                            style={{ width: `${Math.min(volumeUtilization, 100)}%` }}
                          />
                        </div>
                        <div className="text-xs text-muted-foreground text-center">
                          {volumeUtilization.toFixed(1)}%
                        </div>
                      </div>

                      {load.maxPallets && (
                        <div className="space-y-2">
                          <div className="flex items-center justify-between text-sm">
                            <span>Pallets</span>
                            <span className="font-medium">
                              {load.currentPallets} / {load.maxPallets}
                            </span>
                          </div>
                          <div className="h-2 bg-muted rounded-full overflow-hidden">
                            <div
                              className={`h-full transition-all ${
                                palletUtilization > 90
                                  ? 'bg-destructive'
                                  : palletUtilization > 75
                                    ? 'bg-warning-500'
                                    : 'bg-success-500'
                              }`}
                              style={{ width: `${Math.min(palletUtilization, 100)}%` }}
                            />
                          </div>
                          <div className="text-xs text-muted-foreground text-center">
                            {palletUtilization.toFixed(1)}%
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Shipments in Load */}
                <Card>
                  <CardHeader>
                    <h3 className="text-base font-semibold text-foreground">
                      Shipments in Load ({load.shipmentLoads?.length || 0})
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      Drag to reorder the loading sequence
                    </p>
                  </CardHeader>
                  <CardContent>
                    {!load.shipmentLoads || load.shipmentLoads.length === 0 ? (
                      <div className="text-center py-12 text-muted-foreground">
                        <Package className="w-12 h-12 mx-auto mb-4 opacity-50" />
                        <p>No shipments in this load yet</p>
                        <p className="text-sm">Add shipments from the available list</p>
                      </div>
                    ) : (
                      <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleDragEnd}
                      >
                        <SortableContext
                          items={load.shipmentLoads.map((sl) => sl.id)}
                          strategy={verticalListSortingStrategy}
                        >
                          <div className="space-y-2">
                            {load.shipmentLoads.map((shipmentLoad) => (
                              <SortableShipmentCard
                                key={shipmentLoad.id}
                                shipmentLoad={shipmentLoad}
                                onRemove={() => handleRemoveShipment(shipmentLoad.shipmentId)}
                              />
                            ))}
                          </div>
                        </SortableContext>
                      </DndContext>
                    )}
                  </CardContent>
                </Card>
              </>
            ) : (
              <Card>
                <CardContent className="py-16">
                  <div className="text-center text-muted-foreground">
                    <Truck className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p className="text-lg font-medium">No Load Created</p>
                    <p className="text-sm mt-2">
                      Create a new load to start building your shipment plan
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </motion.div>
    </motion.main>
  );
}
